import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';

import { Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';
import { fadeInOut } from '../../../services/animations';
import { StringStringTuple, ReportMetadataService, BusinessCategoryViewModel, BusinessCategoryService, Int32StringTuple } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { CanComponentDeactivate } from '../../../services/deactivate-guard';

@Component({
    selector: 'app-business-category-details',
    templateUrl: './business-category-details.component.html',
    styleUrls: ['./business-category-details.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BusinessCategoryDetailsComponent implements OnInit, CanComponentDeactivate {

    businessCategory: BusinessCategoryViewModel;
    users$: Observable<Array<StringStringTuple>>;
    createdByName$: Observable<string>;
    changedByName$: Observable<string>;

    id: number;
    businessCategoryForm: UntypedFormGroup;

    public isNew = false;
    private isSaving = false;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private businessCategoryService: BusinessCategoryService,
        private reportMetadataService: ReportMetadataService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private snackBar: MatSnackBar,
        private authService: AuthService
    ) {
        this.id = Number.parseInt(this.route.snapshot.paramMap.get('id'));
    }

    ngOnInit(): void {
        this.buildForm();

        this.users$ = this.reportMetadataService.getUserMetadata();

        if (this.id) {
            this.businessCategoryService.getBusinessCategoryById(this.id).subscribe(ok => this.dataLoaded(ok), error => this.alertService.showStickyMessage(error));
        } else {
            this.isNew = true;
            this.businessCategory = this.initializeEmptyObject();
            this.resetForm();
        }
    }

    dataLoaded(businessCategory: BusinessCategoryViewModel) {
        this.businessCategory = businessCategory;

        this.isNew = false;
        this.resetForm();
    }

    public getFormControl(name: string): AbstractControl {
        return this.businessCategoryForm.controls[name];
    }

    private buildForm() {
        this.businessCategoryForm = this.formBuilder.group({
            businessCategoryId: '',
            name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
            enabled: '',
            description: '',
            requirementsNotFulfilledDescription: '',
            requirementsFulfilledDescription: '',
            createdBy: '',
            createdDate: '',
            updatedBy: '',
            updatedDate: '',
        });
    }

    public resetForm() {
        if (!this.businessCategory) {
            this.isNew = true;
            this.businessCategory = this.initializeEmptyObject();
        }

        this.businessCategoryForm.reset({
            businessCategoryId: this.businessCategory.businessCategoryId || '',
            name: this.businessCategory.name || '',
            description: this.businessCategory.description ||'',
            enabled: this.businessCategory.enabled || true,
            requirementsNotFulfilledDescription: this.businessCategory.requirementsNotFulfilledDescription || '',
            requirementsFulfilledDescription: this.businessCategory.requirementsFulfilledDescription || '',
            createdBy: this.businessCategory.createdBy || '',
            createdDate: new DateFormatterPipe().transform(this.businessCategory.createdDate, true) || '',
            updatedBy: this.businessCategory.updatedBy || '',
            updatedDate: new DateFormatterPipe().transform(this.businessCategory.updatedDate, true) || '',
        });

        if (this.businessCategory.createdBy) {
            this.createdByName$ = this.reportMetadataService.getUserById(this.businessCategory.createdBy).pipe(map(t => t.item2));
        }
        if (this.businessCategory.updatedBy) {
            this.changedByName$ = this.reportMetadataService.getUserById(this.businessCategory.updatedBy).pipe(map(t => t.item2));
        }
    }

    private initializeEmptyObject(): BusinessCategoryViewModel {
        var result = <BusinessCategoryViewModel>{};
        return result;
    }

    private prepareSave(): BusinessCategoryViewModel {
        const formModel = this.businessCategoryForm.value;

        return {
            businessCategoryId: this.isNew ? null : formModel.businessCategoryId,
            enabled: formModel.enabled,
            name: formModel.name,
            description: formModel.description,
            requirementsNotFulfilledDescription: formModel.requirementsNotFulfilledDescription,
            requirementsFulfilledDescription: formModel.requirementsFulfilledDescription
        };
    }

    public save() {
        if (!this.businessCategoryForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const businessCategory = this.prepareSave();

        if (this.isNew) {
            this.businessCategoryService.createBusinessCategory(businessCategory).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
        else {
            this.businessCategoryService.updateBusinessCategory(businessCategory.businessCategoryId, businessCategory).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
    }

    public cancel() {
        this.location.back();
    }

    // Prevent leaving the form accidentaly if the form is dirty
    canDeactivate(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.businessCategoryForm.dirty) {
            let snackbar = this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return close.pipe(
                merge(remain),
                take(1));

        } else {
            return of(true);
        }
    }


    private saveCompleted(businessCategory: BusinessCategoryViewModel) {
        this.businessCategory = businessCategory;
        this.id = businessCategory.businessCategoryId;
        this.isNew = false;

        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), businessCategory.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        this.resetForm();
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.changeReferenceData);
    }

}
